import type { ReactNode } from "react";
import clsx from "clsx";
import type { EventTimeFieldsValue } from "./EventTimingFields";
import { EventTimingFields } from "./EventTimingFields";
import { timezoneCity } from "../utils/timezoneDisplay";

export function EventEditorSection({
  id,
  title,
  description,
  icon,
  children,
  className,
}: {
  id: string;
  title: string;
  description?: string;
  icon: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section aria-labelledby={id} className={clsx("min-w-0", className)}>
      <div className="mb-3 flex items-start gap-2.5">
        <span
          className="material-symbols-outlined mt-px text-lg text-secondary"
          aria-hidden
        >
          {icon}
        </span>
        <div className="min-w-0">
          <h4
            id={id}
            className="m-0 text-xs font-extrabold uppercase tracking-wider text-secondary"
          >
            {title}
          </h4>
          {description && (
            <p className="mb-0 mt-0.5 text-xs font-medium text-tertiary">
              {description}
            </p>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

export function EventTimingSection({
  id,
  start,
  end,
  onStartChange,
  onEndChange,
  disabled,
  children,
}: {
  id: string;
  start: EventTimeFieldsValue;
  end: EventTimeFieldsValue;
  onStartChange: (value: EventTimeFieldsValue) => void;
  onEndChange: (value: EventTimeFieldsValue) => void;
  disabled: boolean;
  children?: ReactNode;
}) {
  return (
    <EventEditorSection
      id={id}
      title="Timing"
      description="Start and end use their own wall time and timezone."
      icon="schedule"
    >
      <div className="rounded-xl border border-border bg-subtle p-1.5">
        <div className="mb-1 flex justify-end px-1 pt-1">
          <span className="rounded-md bg-white px-2 py-1 text-2xs font-bold text-tertiary shadow-sm">
            {start.zone === end.zone
              ? timezoneCity(start.zone)
              : `${timezoneCity(start.zone)} → ${timezoneCity(end.zone)}`}
          </span>
        </div>
        <EventTimingFields
          start={start}
          end={end}
          onStartChange={onStartChange}
          onEndChange={onEndChange}
          disabled={disabled}
        />
      </div>
      {children}
    </EventEditorSection>
  );
}

export function EventEditorAlert({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="m-0 rounded-xl border border-error/25 bg-error-bg px-4 py-3 text-sm font-semibold text-error"
    >
      {children}
    </p>
  );
}
